import { Button, Form, Radio, Input, Select } from "antd";

import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { useSearchParams } from "react-router-dom";

import useSpuPoints from "../../../stores/spuPoints";
import { DisplayUnits } from "../../SpuChart/displayUnits/definitions";
import { getDisplayUnitLabel } from "../../SpuChart/displayUnits/converters";
import {
  buildPerformanceCanonicalFormValues,
  buildPerformanceFormDisplayValues
} from "../../SpuChart/displayUnits/adapter";
import {
  VISCOSITY_QUERY_KEYS
} from "../../SpuChart/viscosity/converters";

const { Option } = Select;
const { useForm } = Form;

const Unit = ({ name, style }: { name: string; style?: any }) => {
  return <span style={{ display: "block", width: 30, ...style }}>{name}</span>;
};

let oldQuantity: any = '';
let oldHead: any = '';
let oldSg: any = '';
let oldViscosity: any = '';

interface PerformanceFormProps {
  displayUnits: DisplayUnits;
  setParamsWithDisplayUnits?: (values: Record<string, string | number | undefined>) => void;
}

const PerformanceForm = ({
  displayUnits,
  setParamsWithDisplayUnits
}: PerformanceFormProps) => {
  const [form] = useForm();
  const { t } = useTranslation();

  const [showQEffType, setShowQEffType] = useState(true);

  const { info, performance, setPerformanceValue, resetPerformance, setPerformanceForm } = useSpuPoints();

  const [searchParams, setSearchParams] = useSearchParams();

  const { type } = info;

  const paramQ = Number(searchParams.get("q"));
  const paramH = Number(searchParams.get("h"));
  const paramSg = Number(searchParams.get(VISCOSITY_QUERY_KEYS.sg));
  const paramViscosity = Number(searchParams.get(VISCOSITY_QUERY_KEYS.value));
  const { fieldsValue } = performance;
  const { quantity, head, sg, viscosity } = fieldsValue;
  const displayValues = buildPerformanceFormDisplayValues({
    quantity,
    head,
    viscosity,
    sg
  }, displayUnits);
  const finalSg = sg && Number.isFinite(sg) ? sg : 1;
  const displayViscosity = displayValues.viscosity;

  useEffect(() => {
    // 检查 erpID 是否为 13 或 14
    const aikonLoggedUser = window.localStorage.getItem("aikonLoggedUser");
    if (aikonLoggedUser) {
      try {
        const userData = JSON.parse(aikonLoggedUser);
        const erpID = userData?.erpID;
        
        if (erpID === 13 || erpID === 14) {
          setShowQEffType(false);
        }
      } catch (error) {
      }
    }

    setPerformanceForm(form);

    if (quantity !== undefined && head !== undefined) {
      return;
    }

    if (!Number.isNaN(paramQ) && !Number.isNaN(paramH) && searchParams.has("q") && searchParams.has("h")) {
      setPerformanceValue({
        type: 0,
        quantity: paramQ,
        head: paramH,
        sg: !Number.isNaN(paramSg) && searchParams.has(VISCOSITY_QUERY_KEYS.sg) ? paramSg : 1,
        viscosity:
          !Number.isNaN(paramViscosity) && searchParams.has(VISCOSITY_QUERY_KEYS.value)
            ? paramViscosity
            : undefined
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const lsQEffType = window.localStorage.getItem("qEffType");
    const qEffType = lsQEffType || '3';
    const finalQEffType = Number(qEffType);

    form.setFieldsValue({
      qEffType
    });

    if (!Number.isNaN(paramQ) && !Number.isNaN(paramH) && searchParams.has("q") && searchParams.has("h")) {
      setPerformanceValue({
        type: 0,
        quantity: paramQ,
        head: paramH,
        sg: !Number.isNaN(paramSg) && searchParams.has(VISCOSITY_QUERY_KEYS.sg) ? paramSg : 1,
        viscosity:
          !Number.isNaN(paramViscosity) && searchParams.has(VISCOSITY_QUERY_KEYS.value)
            ? paramViscosity
            : undefined,
        qeff: finalQEffType
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      quantity: displayValues.quantity,
      head: displayValues.head,
      sg: finalSg,
      viscosity: displayViscosity
    });

    oldQuantity = displayValues.quantity !== undefined ? String(displayValues.quantity) : '';
    oldHead = displayValues.head !== undefined ? String(displayValues.head) : '';
    oldSg = finalSg !== undefined ? String(finalSg) : '';
    oldViscosity = displayViscosity !== undefined ? String(displayViscosity) : '';
  }, [displayValues.head, displayValues.quantity, displayViscosity, finalSg, form]);

  const handleSubmit = (values: any) => {
    const {
      type,
      quantity,
      head,
      sg,
      qEffType,
      viscosity
    } = values;

    const canonicalValues = buildPerformanceCanonicalFormValues({
      quantity,
      head,
      viscosity,
      sg
    }, displayUnits);

    if (canonicalValues.quantity === undefined || canonicalValues.head === undefined) return;

    const finalQ: any = Number(canonicalValues.quantity);
    const finalH: any = Number(canonicalValues.head);
    const finalSg = sg ? Number(sg) : 1;
    const finalViscosity = canonicalValues.viscosity;

    setPerformanceValue({
      type,
      quantity: finalQ,
      head: finalH,
      sg: finalSg,
      qeff: qEffType,
      viscosity: finalViscosity
    });

    if (setParamsWithDisplayUnits) {
      setParamsWithDisplayUnits({
        q: finalQ,
        h: finalH,
        [VISCOSITY_QUERY_KEYS.sg]: finalSg,
        [VISCOSITY_QUERY_KEYS.value]: finalViscosity,
      });
    } else if (searchParams.has("q") || searchParams.has("h")) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("q", `${finalQ}`);
      nextParams.set("h", `${finalH}`);
      nextParams.set(VISCOSITY_QUERY_KEYS.sg, `${finalSg}`);

      if (finalViscosity !== undefined) {
        nextParams.set(VISCOSITY_QUERY_KEYS.value, `${finalViscosity}`);
      }
      setSearchParams(nextParams);
    }
  };

  const handleReset = () => {
    form.resetFields();
    resetPerformance();
    if (setParamsWithDisplayUnits) {
      setParamsWithDisplayUnits({
        q: undefined,
        h: undefined,
        [VISCOSITY_QUERY_KEYS.sg]: undefined,
        [VISCOSITY_QUERY_KEYS.value]: undefined,
      });
    } else {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("q");
      nextParams.delete("h");
      nextParams.delete(VISCOSITY_QUERY_KEYS.sg);
      nextParams.delete(VISCOSITY_QUERY_KEYS.value);
      setSearchParams(nextParams);
    }
  };

  const handleQEFFChange = (val: any) => {
    if (val !== null && val !== undefined) {
      window.localStorage.setItem("qEffType", val.toString());
    }
  }

  const handleQuantityBlur = () => {
    let {
      quantity: finalValue
    } = form.getFieldsValue();

    if (finalValue === '') {
      form.setFieldsValue({
        quantity: finalValue
      });
  
      oldQuantity = finalValue;

      return;
    }

    const finalVs = `${finalValue}`.split('.');

    const first = finalVs[0] || '0';
    let two = finalVs[1] || '0';
    two = two.slice(0, 1);

    finalValue = `${first}${two ? '.' : ''}${two}`;

    form.setFieldsValue({
      quantity: finalValue
    });

    oldQuantity = finalValue;
  }

  const handleQuantityChange = (e: any) => {
    const length = oldQuantity.length;

    const value = e.target.value;
    const lengthTwo = value.length
    
    if (lengthTwo > length) {
      let newValue = value.slice(length);
  
      newValue = newValue.replace(/\D/g, '.');
  
      let finalValue = `${oldQuantity}${newValue}`;
  
      form.setFieldsValue({
        quantity: finalValue
      });
  
      oldQuantity = finalValue;
    } else {
      form.setFieldsValue({
        quantity: value
      });
  
      oldQuantity = value;
    }
  }

  const handleHeadBlur = () => {
    let {
      head: finalValue
    } = form.getFieldsValue();

    if (finalValue === '') {
      form.setFieldsValue({
        head: finalValue
      });
  
      oldHead = finalValue;

      return;
    }

    const finalVs = `${finalValue}`.split('.');

    const first = finalVs[0] || '0';
    let two = finalVs[1] || '0';
    two = two.slice(0, 1);

    finalValue = `${first}${two ? '.' : ''}${two}`;

    form.setFieldsValue({
      head: finalValue
    });

    oldHead = finalValue;
  }

  const handleHeadChange = (e: any) => {
    const length = oldHead.length;

    const value = e.target.value;
    const lengthTwo = value.length
    
    if (lengthTwo > length) {
      let newValue = value.slice(length);
  
      newValue = newValue.replace(/\D/g, '.');
  
      let finalValue = `${oldHead}${newValue}`;
  
      form.setFieldsValue({
        head: finalValue
      });
  
      oldHead = finalValue;
    } else {
      form.setFieldsValue({
        head: value
      });
  
      oldHead = value;
    }
  }

  const handleSgBlur = () => {
    let {
      sg: finalValue
    } = form.getFieldsValue();

    if (finalValue === '') {
      form.setFieldsValue({
        sg: finalValue
      });
  
      oldSg = finalValue;

      return;
    }

    const finalVs = `${finalValue}`.split('.');

    const first = finalVs[0] || '0';
    let two = finalVs[1] || '00';
    two = two.slice(0, 2);

    finalValue = `${first}${two ? '.' : ''}${two}`;

    const number = Number(finalValue);

    if (number < 0.2) {
      finalValue = 0.2;
    }

    if (number > 5.0) {
      finalValue = 5.0;
    }

    form.setFieldsValue({
      sg: finalValue
    });

    oldSg = finalValue;
  }

  const handleSgChange = (e: any) => {
    const length = oldSg.length;

    const value = e.target.value;
    const lengthTwo = value.length
    
    if (lengthTwo > length) {
      let newValue = value.slice(length);
  
      newValue = newValue.replace(/\D/g, '.');
  
      let finalValue = `${oldSg}${newValue}`;
  
      form.setFieldsValue({
        sg: finalValue
      });
  
      oldSg = finalValue;
    } else {
      form.setFieldsValue({
        sg: value
      });
  
      oldSg = value;
    }
  }

  const handleViscosityBlur = () => {
    let {
      viscosity: finalValue
    } = form.getFieldsValue();

    if (finalValue === '') {
      form.setFieldsValue({
        viscosity: finalValue
      });
      oldViscosity = finalValue;
      return;
    }

    const finalVs = `${finalValue}`.split('.');
    const first = finalVs[0] || '0';
    let two = finalVs[1] || '00';
    two = two.slice(0, 2);
    finalValue = `${first}${two ? '.' : ''}${two}`;

    form.setFieldsValue({
      viscosity: finalValue
    });
    oldViscosity = finalValue;
  }

  const handleViscosityChange = (e: any) => {
    const length = oldViscosity.length;
    const value = e.target.value;
    const lengthTwo = value.length

    if (lengthTwo > length) {
      let newValue = value.slice(length);

      newValue = newValue.replace(/\D/g, '.');

      const finalValue = `${oldViscosity}${newValue}`;

      form.setFieldsValue({
        viscosity: finalValue
      });

      oldViscosity = finalValue;
    } else {
      form.setFieldsValue({
        viscosity: value
      });

      oldViscosity = value;
    }
  }

  return (
    <Form
      form={form}
      name="settings"
      layout="vertical"
      onReset={handleReset}
      onFinish={handleSubmit}
      initialValues={{ type: 0, sg: 1 }}
    >
      <Form.Item label={t("chart.formula")} name="type">
        <Radio.Group>
          <Radio.Button value={0}>{t("spuForm.formula.select.one")}</Radio.Button>
          <Radio.Button value={1} disabled={!type}>
            {t("spuForm.formula.select.two")}
          </Radio.Button>
        </Radio.Group>
      </Form.Item>
      <Form.Item label="Q" name="quantity" required>
        <Input
          style={{ width: "100%" }}
          addonAfter={<Unit name={getDisplayUnitLabel("flow", displayUnits.flow)} />}
          onChange={handleQuantityChange}
          onBlur={handleQuantityBlur}
        />
      </Form.Item>
      <Form.Item label="H" name="head" required>
        <Input
          style={{ width: "100%" }}
          addonAfter={<Unit name={getDisplayUnitLabel("head", displayUnits.head)} />}
          onChange={handleHeadChange}
          onBlur={handleHeadBlur}
        />
      </Form.Item>
      <Form.Item label={t("chart.sg")} name="sg">
        <Input
          style={{ width: "100%" }}
          onChange={handleSgChange}
          onBlur={handleSgBlur}
        />
      </Form.Item>
      <Form.Item label="Viscosity" name="viscosity">
        <Input
          style={{ width: "100%" }}
          addonAfter={<Unit name={getDisplayUnitLabel("viscosity", displayUnits.viscosity)} style={{ width: 48 }} />}
          onChange={handleViscosityChange}
          onBlur={handleViscosityBlur}
        />
      </Form.Item>
      <Form.Item label={t("spuSelection.eff")} name="qEffType">
        <Select
          onChange={handleQEFFChange}
          disabled={!showQEffType}
        >
          <Option value="2">{t("spuSelection.two")}</Option>
          <Option value="3">{t("spuSelection.three")}</Option>
          <Option value="4">{t("spuSelection.four")}</Option>
          <Option value="5">{t("spuSelection.five")}</Option>
        </Select>
      </Form.Item>
      <Form.Item noStyle>
        <Button type="primary" htmlType="submit">
          {t("chart.submit")}
        </Button>
        <Button type="ghost" htmlType="reset" style={{ marginLeft: 10 }}>
          {t("chart.clear")}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default PerformanceForm;
